import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Star, Briefcase, Clock, Home, ChevronRight, CheckCircle2 } from 'lucide-react';
import { MobileLayout } from '../../components/MobileLayout';
import { Card, CardContent } from '../../components/ui/Card';
import { useScreenInit } from '../../useScreenInit.js';
import { usePlatform, initials } from '../../context/PlatformContext';
const availabilityMeta: Record<string, {
  label: string;
  cls: string;
  dot: string;
}> = {
  disponibile: {
    label: 'Disponibile',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500'
  },
  occupato: {
    label: 'Occupato',
    cls: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500'
  },
  off: {
    label: 'Off',
    cls: 'bg-gray-100 text-gray-500 border-gray-200',
    dot: 'bg-gray-400'
  }
};
const jobStatusMeta: Record<string, {
  label: string;
  cls: string;
}> = {
  da_assegnare: {
    label: 'Da assegnare',
    cls: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  assegnata: {
    label: 'Assegnata',
    cls: 'bg-muted text-muted-foreground border-border'
  },
  in_corso: {
    label: '● In corso',
    cls: 'bg-coral/5 text-coral border-coral/20'
  },
  completata: {
    label: 'Completata',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  }
};
export function CmTeamCleanerDettaglioPage() {
  useScreenInit();
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    cleanerById,
    jobsForCleaner,
    cleanerAvgScore
  } = usePlatform();
  const cleaner = cleanerById(id);
  if (!cleaner) {
    return <MobileLayout data-id="element-2435">
        <div className="text-center py-20" data-id="element-2436">
          <p className="text-sm text-muted-foreground mb-4" data-id="element-2437">
            Cleaner non trovato.
          </p>
          <Link to="/cm/team" className="inline-flex items-center gap-1.5 text-sm font-medium text-coral hover:underline" data-id="element-2438">
            <ArrowLeft size={15} data-id="element-2439" />
            Torna al team
          </Link>
        </div>
      </MobileLayout>;
  }
  const jobs = jobsForCleaner(cleaner.id);
  const attivi = jobs.filter(j => j.status === 'assegnata' || j.status === 'in_corso');
  const completati = jobs.filter(j => j.status === 'completata');
  const avg = cleanerAvgScore(cleaner.name);
  const meta = availabilityMeta[cleaner.availability];
  return <MobileLayout data-id="element-2440">
      <Link to="/cm/team" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-navy transition-colors mb-4" data-id="element-2441">
        <ArrowLeft size={16} data-id="element-2442" />
        Team
      </Link>

      {/* Header */}
      <div className="bg-white border border-border rounded-xl shadow-sm p-4 lg:p-5 mb-6" data-id="element-2443">
        <div className="flex items-start gap-4" data-id="element-2444">
          <span className="w-16 h-16 rounded-full bg-coral/10 flex items-center justify-center text-xl font-bold text-coral flex-shrink-0" data-id="element-2445">
            {initials(cleaner.name)}
          </span>
          <div className="flex-1 min-w-0" data-id="element-2446">
            <div className="flex items-center gap-2 flex-wrap mb-1" data-id="element-2447">
              <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-2448">
                {cleaner.name}
              </h1>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${meta.cls}`} data-id="element-2449">
                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} data-id="element-2450" />
                {meta.label}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground" data-id="element-2451">
              <MapPin size={14} className="flex-shrink-0" data-id="element-2452" />
              {cleaner.zones.length > 0 ? cleaner.zones.join(', ') : '—'}
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6" data-id="element-2453">
        <Card data-id="element-2454">
          <CardContent className="text-center" data-id="element-2455">
            <div className="w-9 h-9 rounded-lg bg-coral/10 text-coral flex items-center justify-center mx-auto mb-2" data-id="element-2456">
              <Briefcase size={18} data-id="element-2457" />
            </div>
            <div className="text-2xl font-bold text-navy leading-tight" data-id="element-2458">
              {attivi.length}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5" data-id="element-2459">Attivi</div>
          </CardContent>
        </Card>
        <Card data-id="element-2460">
          <CardContent className="text-center" data-id="element-2461">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-2" data-id="element-2462">
              <CheckCircle2 size={18} data-id="element-2463" />
            </div>
            <div className="text-2xl font-bold text-navy leading-tight" data-id="element-2464">
              {completati.length}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5" data-id="element-2465">
              Completati
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-2466">
          <CardContent className="text-center" data-id="element-2467">
            <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-500 flex items-center justify-center mx-auto mb-2" data-id="element-2468">
              <Star size={18} data-id="element-2469" />
            </div>
            <div className="text-2xl font-bold text-navy leading-tight" data-id="element-2470">
              {avg ?? '—'}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5" data-id="element-2471">
              Score medio
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Assigned jobs */}
      <h2 className="text-sm font-semibold text-navy flex items-center gap-2 mb-3" data-id="element-2472">
        <Briefcase size={16} className="text-coral" data-id="element-2473" />
        Incarichi
      </h2>
      {jobs.length > 0 ? <div className="space-y-3" data-id="element-2474">
          {jobs.map(j => {
        const sm = jobStatusMeta[j.status];
        return <Card key={j.id} className="hover:border-coral/40 hover:shadow-sm transition-all cursor-pointer" onClick={() => navigate(`/cm/clienti/appartamenti/${j.apartmentId}`)} data-id="element-2475">
                <CardContent className="flex items-center justify-between gap-3" data-id="element-2476">
                  <div className="min-w-0" data-id="element-2477">
                    <div className="flex items-center gap-2 mb-1" data-id="element-2478">
                      <span className="text-sm font-semibold text-foreground truncate" data-id="element-2479">
                        {j.apartmentName}
                      </span>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border flex-shrink-0 ${sm.cls}`} data-id="element-2480">
                        {sm.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap" data-id="element-2481">
                      <span className="flex items-center gap-1" data-id="element-2482">
                        <Home size={12} data-id="element-2483" />
                        {j.type}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-2484">
                        <Clock size={12} data-id="element-2485" />
                        {j.date} · {j.time}
                      </span>
                      <span data-id="element-2486">€{j.earning}</span>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-gray-300 flex-shrink-0" data-id="element-2487" />
                </CardContent>
              </Card>;
      })}
        </div> : <div className="bg-white border border-border rounded-xl p-8 text-center" data-id="element-2488">
          <p className="text-sm text-muted-foreground" data-id="element-2489">
            Nessun incarico assegnato a {cleaner.name}.
          </p>
        </div>}
    </MobileLayout>;
}